import _ from 'underscore';
import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { Stack } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Loading } from '@bingads-webui-campaign-react/loading';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { Collapse, useCollapseState, ValidatedPanel, collapseExpander } from '@bingads-webui-campaign/wizard-jumbo-card';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { campaignNameProp } from '@bingads-webui-campaign-react/xandr-campaign';
import { ValidatedUrlOptionsParameter } from '@bingads-webui-campaign-react/validated-url-input';
import { BaseExclusion, getExclusionsDetails } from '@bingads-webui-campaign/hotel-common-upsert';
import { getUrlOptionsDetails } from '@bingads-webui-campaign-react/url-options';
import {
  StepHeader,
  conversionGoalsIcon,
  budgetBidStrategyIcon,
  AudienceBudgetBidStrategy,
  SectionShimmer,
} from '@bingads-webui-campaign-react/display-plus-campaigns';
import { ShoppingImg, UrlOptionsImg } from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { ProductImg } from '@bingads-webui-campaign-react/sponsored-campaign-creation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { useFormService } from '@bingads-webui-react/form';
import { ValidatedConversionGoals } from '@bingads-webui-campaign-react/step-wizard-controls';
import { useWizardService, matchStepState, STEP_STATES, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import biddingStrategyTypes from '@bingads-webui-campaign-legacy/bidding-strategy/bidding-strategy-type';
import { getConversionGoalTitleDetails, getStoreTitleDetails, getBidBudgetTitleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { getStores, getCountries } from '@bingads-webui-campaign-react/store-select';
import { getMessageType } from '@bingads-webui-campaign-react/campaign-settings-utils';

import { ValidatedProductFilterSetting, ValidatedStoreSetting, BMCRedirect, CampaignPriority } from './component';
import { getCampaignStyles } from './styles';
import { isSearchShoppingCampaign, getProductTileDetails, getNoValidStore } from '../step-utils';
import { CREATE_SCENARIO } from '../../constants';
import { TextTitle } from '../../shared/pannel-header';

// todo: refer to private\ui-next\packages\display-plus-campaigns\src\steps\campaign\campaign-step-shell.js
const getPanelErrorProps = ({ needsAttention }) => (needsAttention ? {
  error: _TL_("* Something's wrong"),
} : {});

const getPanelOptions = () => ({
  basicOptions: [
    'campaign-name',
    'store-settings',
    'product-filter',
    'conversions',
    'bid-and-budget',
    'campaign-url-options',
    'base-exclusion',
  ],
});

export const CampaignSettingsStep = (props) => {
  const {
    data,
    onDataChange,
    error,
  } = props;

  const {
    campaign,
    productData,
    feedData,
    productFilterRef,
    storeSettingRef,
    setSiteExclusionView,
    setIPExclusionView,
    siteExclusionViewRef,
    ipExclusionViewRef,
    productConditionsRef,
  } = data || {};

  const classes = useDefaultStyles(getCampaignStyles);

  const serverContext = useContext(ServerContext);
  const isEnabledForDynamicFeedCampaign = _.get(serverContext, ['Permissions', 'IsEnabledForDynamicFeedCampaign'], false);
  const isConversionAssociationWarningP3Enabled = _.get(serverContext, ['DynamicPermissions', 'ConversionAssociationWarningP3'], false);

  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid } = scope.values;

  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);
  const globalLegacyI18n = useMemo(() => window.globalLegacyI18n.get('$/i18n!legacy/global'), []);
  const campaignBiddingSchemeType = _.get(campaign, ['BiddingScheme', 'Type'], '');
  const activity = useContext(ActivityContext);

  const { state, send } = useWizardService();
  const { wizardData } = state.context || {};
  const { flow, createScenario, campaignStepIsValid } = wizardData || {};
  const isEditMode = createScenario === CREATE_SCENARIO.UPDATE_CAMPAIGN;
  const currentStates = state.toStrings()[1];
  const isSearch = useMemo(() => isSearchShoppingCampaign(flow), [flow]);

  const [isCPSSelected, setIsCPSSelected] = useState(false);
  const { basicOptions } = getPanelOptions();
  const [basicOptionsShown, setBasicOptionsShown] = useState(basicOptions);
  const [productLoading, setProductLoading] = useState(false);

  const noStore = useMemo(() => getNoValidStore(productData, feedData), [productData, feedData]);

  // add campaign here to ensure data loaded before showing redirect to BMC
  const showRedirectToBMC = useMemo(() => campaign && noStore && !isEditMode, [noStore, campaign, isEditMode]);

  const [
    allCampaignSettingsSections,
    campaignSettingsExpandedSections,
    setCampaignSettingsExpandedSections,
  ] = useCollapseState(
    basicOptionsShown /* all sections */,
    ['campaign-name', 'conversions', 'bid-and-budget', 'store-settings',
      'product-filter', 'conversions', 'campaign-url-options',
      'base-exclusion'] /* initially expanded sections */
  );

  const basicSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setCampaignSettingsExpandedSections }),
    [setCampaignSettingsExpandedSections]
  );

  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();

  const onValidate = (result) => {
    if (data && data.campaign) {
      const resultBiddingScheme = _.get(result, ['data', 'BiddingScheme'], {});
      const campaignBiddingScheme = _.get(campaign, ['BiddingScheme'], {});
      let biddingScheme = resultBiddingScheme;

      if (resultBiddingScheme && !resultBiddingScheme.Type && !_.isUndefined(campaignBiddingSchemeType)) {
        biddingScheme = campaignBiddingScheme;
      }

      const patchData = {
        ...data,
        campaign: {
          ...data.campaign,
          ...result.data,
          BiddingScheme: {
            ...biddingScheme,
          },
        },
      };

      setBasicOptionsShown([...basicOptions]);
      onDataChange({
        data: {
          ...patchData,
          adGroup: {
            ...data.adGroup,
          },
        },
        isValid: result.isValid,
      });

      if (isConversionAssociationWarningP3Enabled && isEditMode) {
        const message = getMessageType(data, patchData);
        if (message) {
          const updatedData = {
            wizardData: {
              ...wizardData,
              customConfirmMessages: [message],
            },
          };
          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, updatedData.wizardData);
        }
      }
    }
  };

  const onStoreSelectionChange = useCallback((store) => {
    if (productFilterRef.current) {
      productFilterRef.current.onSelectionChange(store);
    } else {
      console.log('productFilterRef.current is not available');
    }
  }, [productFilterRef]);

  const onFeedLabelChange = useCallback((feedLabel) => {
    productFilterRef.current.triggerFeedLabelChange(feedLabel);
    productFilterRef.current.removeProductGroup();
  }, [productFilterRef]);

  const onSalesCountryChange = useCallback((country) => {
    productFilterRef.current.triggerSalesCountryChange(country);
  }, [productFilterRef]);

  const onSelectFilterProducts = useCallback((isSelected) => {
    if (!isSelected) {
      productFilterRef.current.triggerFeedLabelChange(null);
      productFilterRef.current.triggerIsFeedFilterChecked(false);
    }
  }, [productFilterRef]);

  const updateProductConditionOptions = useCallback((options) => {
    productConditionsRef.current = options;
  }, [productConditionsRef]);

  const updateChildLevelProductCondition = useCallback((option) => {
    if (option) {
      const updatedProductCondition = _.reject(productConditionsRef.current, item => item.Value === option.Value);
      productConditionsRef.current = [...updatedProductCondition, option];
    }
  }, [productConditionsRef]);

  useEffect(() => {
    // since we don't call getProductScope to get the correct country product data if customer selected All products and selected country.
    // so we should trigger manually to let it send call when change from All products to filter product.
    if (!isEditMode && campaign && !campaign.CampaignProductScope && campaign.SalesCountry && productFilterRef && productFilterRef.current) {
      const hasFilteredProducts = productFilterRef.current.hasFilteredProducts();
      if (!hasFilteredProducts) {
        productFilterRef.current.triggerSalesCountryChange(campaign.SalesCountry);
      }
    }
  }, [campaign, productFilterRef, isEditMode]);

  const campaignNameTitleDetails = useMemo(() => {
    let campaignDetail = '';
    if (campaign) {
      campaignDetail = i18n.getString(_TL_('Name: {{Name}}'), { Name: campaign.Name });
      if (isSearch) {
        campaignDetail += i18n.getString(_TL_('. Priority: {{Priority}}'), { Priority: campaign.Priority });
      }
    }
    return campaignDetail;
  }, [campaign, i18n, isSearch]);

  const campaignUrlOptions = useMemo(() => {
    if (campaign) {
      return {
        TrackingUrlTemplate: campaign.TrackingUrlTemplate,
        FinalUrlSuffix: campaign.FinalUrlSuffix,
        UrlCustomParameters: campaign.UrlCustomParameters,
      };
    }

    return {
      TrackingUrlTemplate: '',
      FinalUrlSuffix: '',
      UrlCustomParameters: [],
    };
  }, [campaign]);

  const conversionGoalsTitleDetails = useMemo(() => getConversionGoalTitleDetails(i18n, campaign && campaign.conversionGoals), [campaign, i18n]);

  const storeTitleDetails = useMemo(() => {
    if (!campaignSettingsExpandedSections.includes('store-settings') && storeSettingRef && campaign) {
      const storeView = storeSettingRef.current;
      const storesData = {
        stores: getStores({ productData }),
        countries: getCountries({
          productData,
          legacyI18n: globalLegacyI18n,
          IsDomesticSimplifiedChineseEnabled: _.get(serverContext, ['Permissions', 'IsDomesticSimplifiedChineseEnabled'], false),
        }),
        errors: undefined,
      };
      return getStoreTitleDetails(i18n, storesData, storeView.viewModel.toObject(), !isSearch);
    }
    return '';
  }, [i18n, campaign, globalLegacyI18n, productData, serverContext, storeSettingRef, campaignSettingsExpandedSections, isSearch]);

  const productFilterTitleDetails = useMemo(() => {
    if (!campaignSettingsExpandedSections.includes('product-filter') && productFilterRef && campaign) {
      const productScope = productFilterRef.current.getProductScope();
      const hasFilteredProducts = productFilterRef.current.hasFilteredProducts();
      if (!hasFilteredProducts) {
        return i18n.getString(_TL_('All products'));
      }
      return getProductTileDetails(productScope);
    }
    return '';
  }, [campaign, productFilterRef, campaignSettingsExpandedSections, i18n]);

  const exclusionTitleDetails = useMemo(() => {
    if (isEditMode && !campaignSettingsExpandedSections.includes('base-exclusion')) {
      const exclusions = {};
      if (siteExclusionViewRef && siteExclusionViewRef.current) {
        exclusions.ExcludedSites = siteExclusionViewRef.current.getValues();
      }
      if (ipExclusionViewRef && ipExclusionViewRef.current) {
        exclusions.ExcludedIPs = ipExclusionViewRef.current.getValues();
      }
      return getExclusionsDetails(i18n, exclusions);
    }
    return '';
  }, [i18n, siteExclusionViewRef, ipExclusionViewRef, isEditMode, campaignSettingsExpandedSections]);

  const urlTitleDetails = useMemo(() => (isEditMode && campaign ? getUrlOptionsDetails(i18n, campaign) : ''), [isEditMode, campaign, i18n]);

  const budgetBidStrategyTitleDetails = useMemo(() => (
    isEditMode && campaign ? getBidBudgetTitleDetails(serverContext, i18n, campaign.BiddingScheme, campaign.Budget) : ''
  ), [campaign, i18n, serverContext, isEditMode]);

  const onCampaignNameChange = useCallback((ev) => {
    activity.signal(`onCampaignNameChange: ${_.get(ev, ['target', 'value'])}}`);
  }, [activity]);

  useEffect(() => {
    const prefixTitle = isEditMode ? 'onCampaignSettingPage' : 'onCampaignStep';
    if (activity && matchStepState(state, STEP_STATES.LOADING)) {
      activity.signal(`${prefixTitle}Loading - ${currentStates}`);
    }

    if (activity && matchStepState(state, STEP_STATES.SAVING)) {
      activity.signal(`${prefixTitle}Saving - ${currentStates}`);
    }
  }, [activity, currentStates, state, isEditMode]);

  useEffect(() => {
    if (campaign && campaign.BiddingScheme) {
      setIsCPSSelected(campaign.BiddingScheme.Type === biddingStrategyTypes.costPerSale);
    }
  }, [campaign]);

  useEffect(() => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { showRedirectToBMC });
  }, [showRedirectToBMC, send]);

  useEffect(() => {
    const isSaving = matchStepState(state, STEP_STATES.SAVING);
    if (isSaving && campaignStepIsValid === false && campaignSettingsExpandedSections.length === basicOptions.length) {
      setCampaignSettingsExpandedSections([...basicOptions]);
    }
  }, [campaignStepIsValid, setCampaignSettingsExpandedSections, basicOptions, send, state, campaignSettingsExpandedSections]);

  return (
    <div className={classNames(classes.campaignShell, 'campaign-shell')}>
      { showRedirectToBMC ? <BMCRedirect i18n={i18n} scope={scope} isEnabledForDynamicFeedCampaign={isEnabledForDynamicFeedCampaign} activity={activity} /> :
      <ActuationGroup actuationProps={{ isActive: false }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: error || [], showAllValidationErrors }}>
            <ValidationGroup
              validationProps={{
                schema: Campaign,
                onValidate,
                data: campaign || {},
                i18n: legacyI18n,
            }}
            >
              <div className={classNames('campaign-step')}>
                {!isEditMode &&
                <div>
                  <Stack token={{ childrenGap: 14 }}>
                    <StepHeader
                      title={_TL_('Shopping settings')}
                      description={_TL_('Choose which products to show in your ads. Some of your ads will use images, headlines and descriptions from Merchant Center.')}
                    />
                  </Stack>
                </div>}
                <div className="server-errors">
                  <UnmappedFormErrors />
                </div>
                <Stack horizontal token={{ childrenGap: 14 }}>
                  <Collapse allPanelItems={allCampaignSettingsSections} checkItems skipItemsTypeCheck activeKeys={campaignSettingsExpandedSections} setActiveKeys={setCampaignSettingsExpandedSections}>
                    <ValidatedPanel
                      key="campaign-name"
                      title={<TextTitle titleKey={_TL_('Campaign name')} topicKey="pop_BA_CampaignCreate_Settings_CampaignName" details={campaignNameTitleDetails} />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    >
                      <RemainingCharsValidatedTextField
                        placeholder={_TL_('Enter a name for your campaign')}
                        id="input-campaign-name"
                        validationProps={{
                          field: 'Name',
                          schema: Campaign,
                        }}
                        i18n={i18n}
                        formErrorProps={{ filter: campaignNameProp }}
                        onBlur={onCampaignNameChange}
                      />
                      {isSearch && campaign && !isCPSSelected &&
                        <CampaignPriority
                          activity={activity}
                          i18n={i18n}
                          data={data}
                          onDataChange={onDataChange}
                        />
                      }
                    </ValidatedPanel>
                    {
                      isEditMode &&
                      <ValidatedPanel
                        key="bid-and-budget"
                        title={
                          <TextTitle
                            titleKey={_TL_('Budget and Bid Strategy')}
                            icon={budgetBidStrategyIcon}
                            topicKey="pop_BA_CampaignCreate_Budget_DefaultBid"
                            details={budgetBidStrategyTitleDetails}
                          />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      >
                        {
                          campaign ? (
                            <AudienceBudgetBidStrategy
                              data={data}
                              onDataChange={onDataChange}
                              campaignType={campaign.CampaignType.toLowerCase()}
                              campaignSubType={campaign.CampaignSubType}
                            />
                          ) : (<SectionShimmer count={3} />)
                        }
                      </ValidatedPanel>
                    }
                    <ValidatedPanel
                      key="store-settings"
                      title={<TextTitle titleKey={_TL_('Store settings')} icon={ShoppingImg} details={storeTitleDetails} />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      actuationProps={{
                        expand: basicSettingsExpand,
                        expandKey: 'store-settings',
                      }}
                    >
                      {campaign && storeSettingRef && <ValidatedStoreSetting
                        storeSettingRef={storeSettingRef}
                        productData={productData}
                        isSearch={isSearch}
                        feedData={feedData}
                        campaignSettings={campaign}
                        onStoreSelectionChange={onStoreSelectionChange}
                        onSalesCountryChange={onSalesCountryChange}
                        onFeedLabelChange={onFeedLabelChange}
                        onSelectFilterProducts={onSelectFilterProducts}
                        isCPSSelected={isCPSSelected}
                      />}
                    </ValidatedPanel>
                    <ValidatedPanel
                      key="product-filter"
                      title={<TextTitle
                        titleKey={_TL_('Products')}
                        icon={ProductImg}
                        details={productFilterTitleDetails}
                      />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      actuationProps={{
                        expand: basicSettingsExpand,
                        expandKey: 'product-filter',
                      }}
                    >
                      {campaign && productFilterRef &&
                        <ValidatedProductFilterSetting
                          productFilterRef={productFilterRef}
                          productData={productData}
                          campaignSettings={campaign}
                          isSearch={isSearch}
                          feedData={feedData}
                          setProductLoading={setProductLoading}
                          updateProductConditionOptions={updateProductConditionOptions}
                          updateChildLevelProductCondition={updateChildLevelProductCondition}
                        /> }
                      <Loading isVisible={productLoading} />
                    </ValidatedPanel>
                    {
                      isEditMode &&
                      <ValidatedPanel key="base-exclusion" title={<TextTitle titleKey={_TL_('Website & IP address exclusions')} details={exclusionTitleDetails} />}>
                        {campaign &&
                        <BaseExclusion
                          patchData={_.noop}
                          key="base-exclusion"
                          setSiteExclusionView={setSiteExclusionView}
                          setIPExclusionView={setIPExclusionView}
                          options={{
                            level: 'campaign',
                            campaignId: campaign.Id,
                            adgroupId: null,
                            showSiteExclusion: true,
                            showIPExclusion: true,
                          }}
                        />}
                      </ValidatedPanel>
                    }
                    {
                      isEditMode &&
                      <ValidatedPanel
                        key="campaign-url-options"
                        title={<TextTitle titleKey={_TL_('Campaign URL options')} icon={UrlOptionsImg} details={urlTitleDetails} />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                        actuationProps={{
                          expand: basicSettingsExpand,
                          expandKey: 'campaign-url-options',
                        }}
                      >
                        <div className={classes.urlOptionsContainer}>
                          <span className="url-options-descriptions">
                            {i18n.getString(_TL_('Set up the tracking and redirect information for your ads.'))}
                          </span>
                          {campaign &&
                            <ValidatedUrlOptionsParameter
                              i18n={i18n}
                              validationProps={{
                                initialValue: campaignUrlOptions,
                                data: campaignUrlOptions,
                              }}
                            />}
                        </div>
                      </ValidatedPanel>
                    }
                    <ValidatedPanel
                      key="conversions"
                      title={<TextTitle
                        titleKey={_TL_('Conversions')}
                        topicKey="pop_BA_PMAX_Conversions"
                        icon={conversionGoalsIcon}
                        details={conversionGoalsTitleDetails}
                      />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      actuationProps={{
                        expand: basicSettingsExpand,
                        expandKey: 'conversions',
                      }}
                    >
                      <ValidatedConversionGoals
                        validationProps={{
                          initialValue: campaign,
                          data: campaign,
                        }}
                        cid={cid}
                        aid={aid}
                      />
                    </ValidatedPanel>
                  </Collapse>
                </Stack>
              </div>
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
      }
    </div>
  );
};

CampaignSettingsStep.propTypes = {
  data: PropTypes.shape({
    adGroup: PropTypes.shape({}),
    campaign: PropTypes.shape({}),
    productData: PropTypes.shape({}),
    productFilterRef: PropTypes.shape({}),
    feedData: PropTypes.shape({}),
    storeSettingRef: PropTypes.shape({}),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.arrayOf(PropTypes.string),
};

CampaignSettingsStep.defaultProps = {
  error: undefined,
};

